import { Spacing } from '@persona-id/design-tokens';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import Checkbox from 'design-system/components/Checkbox';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer';
import { Span } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { FontWeight } from 'design-system/variables/text';

export interface CountryMatchingFormData extends Record<string, unknown> {
  'match-birthplace'?: boolean;
  'match-citizenship'?: boolean;
  'match-nationality'?: boolean;
  'match-residency'?: boolean;
}

interface CountryMatchingFormSectionProps<
  T extends CountryMatchingFormData = CountryMatchingFormData,
> {
  defaultValues: T;
  disabled: boolean;
}

export const CountryMatchingFormSection = ({
  disabled,
  defaultValues,
}: CountryMatchingFormSectionProps) => {
  const { register } = useFormContext<CountryMatchingFormData>();
  return (
    <RadioGroup>
      <StyledCheckbox
        label={
          <DisplayWithSubtext
            displayText={CountryMatchConfigurationLabel.MatchBirthplace}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            subtextText={CONFIGURATION_SUBTEXT[CountryMatchConfigurationLabel.MatchBirthplace]}
          />
        }
        {...register('match-birthplace')}
        disabled={disabled}
        defaultChecked={defaultValues['match-birthplace']}
      />
      <Spacer height={0.5} />
      <StyledCheckbox
        label={
          <DisplayWithSubtext
            displayText={CountryMatchConfigurationLabel.MatchCitizenship}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            subtextText={CONFIGURATION_SUBTEXT[CountryMatchConfigurationLabel.MatchCitizenship]}
          />
        }
        {...register('match-citizenship')}
        disabled={disabled}
        defaultChecked={defaultValues['match-citizenship']}
      />
      <Spacer height={0.5} />
      <StyledCheckbox
        label={
          <DisplayWithSubtext
            displayText={CountryMatchConfigurationLabel.MatchNationality}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            subtextText={CONFIGURATION_SUBTEXT[CountryMatchConfigurationLabel.MatchNationality]}
          />
        }
        {...register('match-nationality')}
        disabled={disabled}
        defaultChecked={defaultValues['match-nationality']}
      />
      <Spacer height={0.5} />
      <StyledCheckbox
        label={
          <DisplayWithSubtext
            displayText={CountryMatchConfigurationLabel.MatchResidency}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            subtextText={CONFIGURATION_SUBTEXT[CountryMatchConfigurationLabel.MatchResidency]}
          />
        }
        {...register('match-residency')}
        disabled={disabled}
        defaultChecked={defaultValues['match-residency']}
      />
    </RadioGroup>
  );
};

const DisplayWithSubtext = ({
  displayText,
  subtextText,
}: {
  displayText: string;
  subtextText: string;
}) => {
  return (
    <WrapperWithSubtext>
      <ChecklistLabelTitle>{displayText}</ChecklistLabelTitle>
      <Spacer height={0.5} />
      <SubtextSubTitle color='secondary'>{subtextText}</SubtextSubTitle>
    </WrapperWithSubtext>
  );
};

const WrapperWithSubtext = styled.span`
  display: grid;
  grid-template-columns: auto ${Spacing[4]};
  grid-gap: ${Spacing[1]};
  align-items: center;
  color: ${({ theme }) => theme.color.text.primary};
  ${paragraphMixins.md}
`;

const SubtextSubTitle = styled(Span)`
  ${paragraphMixins.md}
  font-weight: ${FontWeight.Regular};
`;

enum CountryMatchConfigurationLabel {
  MatchBirthplace = 'Birthplace',
  MatchCitizenship = 'Citizenship',
  MatchNationality = 'Nationality',
  MatchResidency = 'Residency',
}

const CONFIGURATION_SUBTEXT: { [key in CountryMatchConfigurationLabel]?: string } = {
  [CountryMatchConfigurationLabel.MatchBirthplace]:
    'Country where an individual was born, as specified in the source.',
  [CountryMatchConfigurationLabel.MatchCitizenship]:
    "Country where an individual is registered as a citizen, as specified in the source. One can hold citizenship in more than one country and it can change over one's lifetime.",
  [CountryMatchConfigurationLabel.MatchNationality]:
    "Country where an individual or their parents are born, as specified in the source. One's nationality remains the same throughout their lifetime as it is inherited or innate.",
  [CountryMatchConfigurationLabel.MatchResidency]:
    'Country where an individual resides, as specified in the source.',
};

const ChecklistLabelTitle = styled(Span)`
  ${paragraphMixins.lgBold}
`;

const StyledCheckbox = styled(Checkbox)`
  align-items: flex-start;
  & > div {
    margin-right: ${Spacing[2]};
  }
`;
